import { Signal } from "@/constants/signal/signal"
import type { ReviewWithSignalsAndCompanyId } from "@/db/queries/parsed-review"

export enum WorkType {
  SOLAR = "solar",
  BATTERY = "battery",
  ROOFING = "roofing",
  ELECTRICAL = "electrical",
  SERVICE = "service",
  COMPLEX = "complex",
}

type ScopeSignalOutput = {
  scopes_detected?: string[]
  work_type?: string
}

function getScopesDetected(review: ReviewWithSignalsAndCompanyId): string[] {
  const scopeSignal = review.signals?.[Signal.SCOPE] as ScopeSignalOutput
  return scopeSignal?.scopes_detected ?? []
}

function getWorkTypeFromReview(review: ReviewWithSignalsAndCompanyId) {
  const scopeSignal = review.signals?.[Signal.SCOPE] as ScopeSignalOutput
  return scopeSignal?.work_type
}

export type WorkTypeConfig = {
  name: string
  description: string
  icon: string
  iconSize: number // Adjust per icon since PNGs have different padding
  matchesReview: (review: ReviewWithSignalsAndCompanyId) => boolean
}

export const dataByWorkType: Record<WorkType, WorkTypeConfig> = {
  [WorkType.SOLAR]: {
    name: "SOLAR",
    description: "Installation, permitting, and grid connection.",
    icon: "/assets/work-types/solar.png",
    iconSize: 70,
    matchesReview: (r) => getScopesDetected(r).includes("solar"),
  },
  [WorkType.BATTERY]: {
    name: "BATTERY",
    description: "Energy storage for backup savings and independence.",
    icon: "/assets/work-types/battery.png",
    iconSize: 60,
    matchesReview: (r) => getScopesDetected(r).includes("battery"),
  },
  [WorkType.ROOFING]: {
    name: "ROOFING",
    description: "Repair or replacement, before or after solar installation.",
    icon: "/assets/work-types/roofing.png",
    iconSize: 70,
    matchesReview: (r) => {
      const scopes = getScopesDetected(r)
      return scopes.includes("roofing") || scopes.includes("solar_roofing")
    },
  },
  [WorkType.ELECTRICAL]: {
    name: "ELECTRICAL",
    description: "Panel upgrades and wiring for system readiness.",
    icon: "/assets/work-types/electrical.png",
    iconSize: 40,
    matchesReview: (r) => {
      const scopes = getScopesDetected(r)
      return (
        scopes.includes("electrical") ||
        scopes.includes("hvac") ||
        scopes.includes("other")
      )
    },
  },
  [WorkType.SERVICE]: {
    name: "SERVICE",
    description: "Repairs, maintenance, and ongoing system support.",
    icon: "/assets/work-types/service.png",
    iconSize: 60,
    matchesReview: (r) => getWorkTypeFromReview(r) === "service",
  },
  [WorkType.COMPLEX]: {
    name: "COMPLEX PROJECTS",
    description: "Multi-trade installations requiring co-ordination.",
    icon: "/assets/work-types/complex.png",
    iconSize: 50,
    matchesReview: (r) => getScopesDetected(r).length >= 3,
  },
}

export const WORK_TYPE_ORDER = [
  WorkType.SOLAR,
  WorkType.BATTERY,
  WorkType.ROOFING,
  WorkType.ELECTRICAL,
  WorkType.SERVICE,
  WorkType.COMPLEX,
]
